import { clsx } from "clsx";
import { PanelsTopLeft, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMediaQuery } from "@/shared/lib/hooks";
import { t, useLocale } from "@/shared/lib/i18n";
import { buttonClassName, IconButton } from "@/shared/ui/primitives/Button";

/**
 * The dock for minimised windows.
 *
 * Exognitos has more than one window-shaped surface — the Focus tool, the note
 * workbench, a task/plan/routine/map detail opened over its source — and each of
 * them can be put aside without being thrown away. A minimised window is not
 * closed: it keeps its state and stays one click from the surface it belongs to.
 *
 * The dock owns no window content. It only holds the chips that bring one back,
 * so a feature can gain "minimise" by registering here instead of inventing a
 * second floating bar.
 */

export type DockedWindow = {
  id: string;
  /** Short uppercase family label — "Not", "Görev", "Plan". */
  kind: string;
  title: string;
  onRestore: () => void;
  onClose?: () => void;
};

type WindowDockValue = {
  windows: readonly DockedWindow[];
  dock: (entry: DockedWindow) => void;
  undock: (id: string) => void;
};

const WindowDockContext = createContext<WindowDockValue | null>(null);

export function useWindowDock(): WindowDockValue | null {
  return useContext(WindowDockContext);
}

export function WindowDockProvider({ children }: { children: ReactNode }) {
  useLocale();
  const [windows, setWindows] = useState<DockedWindow[]>([]);

  const dock = useCallback((entry: DockedWindow) => {
    setWindows((current) => {
      const index = current.findIndex((item) => item.id === entry.id);
      if (index < 0) return [...current, entry];
      const next = [...current];
      next[index] = entry;
      return next;
    });
  }, []);

  const undock = useCallback((id: string) => {
    setWindows((current) =>
      current.some((item) => item.id === id) ? current.filter((item) => item.id !== id) : current,
    );
  }, []);

  const value = useMemo(() => ({ windows, dock, undock }), [dock, undock, windows]);

  return <WindowDockContext.Provider value={value}>{children}</WindowDockContext.Provider>;
}

/**
 * Registers a window in the dock for as long as `entry` is supplied.
 *
 * Callbacks are read through a ref so a parent re-render does not churn the
 * dock; only the identity and the label participate in the effect.
 */
export function useDockedWindow(entry: DockedWindow | null) {
  useLocale();
  const dockApi = useWindowDock();
  const handlers = useRef<Pick<DockedWindow, "onClose" | "onRestore">>({
    onRestore: () => {},
  });
  handlers.current = { onRestore: entry?.onRestore ?? (() => {}), onClose: entry?.onClose };

  const id = entry?.id;
  const kind = entry?.kind ?? t("copy.38ed0d380a85");
  const title = entry?.title;
  const closable = Boolean(entry?.onClose);
  const dock = dockApi?.dock;
  const undock = dockApi?.undock;

  useEffect(() => {
    if (!dock || !undock || !id) return;
    dock({
      id,
      kind,
      title: title ?? "",
      onRestore: () => handlers.current.onRestore(),
      onClose: closable ? () => handlers.current.onClose?.() : undefined,
    });
    return () => undock(id);
  }, [closable, dock, id, kind, title, undock]);
}

/** The visible row of minimised windows. Rendered once, by the shell. */
export function WindowDockBar({ className }: { className?: string }) {
  useLocale();
  const dockApi = useWindowDock();
  const windows = dockApi?.windows ?? [];
  const desktop = useMediaQuery("(min-width: 768px)");
  const selector = useRef<HTMLDetailsElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (windows.length === 0) {
      root.style.setProperty("--ex-window-dock-height", "0px");
      return () => root.style.removeProperty("--ex-window-dock-height");
    }

    const reserveSpace = () => {
      const measuredHeight = bar.current?.getBoundingClientRect().height ?? 0;
      // jsdom and the first pre-layout browser pass report zero. The selector
      // is 40 px tall, so 3.5rem remains the safe one-row fallback. Wrapped
      // desktop docks publish their real height plus the same 1rem clearance.
      root.style.setProperty(
        "--ex-window-dock-height",
        measuredHeight > 0 ? `${Math.ceil(measuredHeight + 16)}px` : "3.5rem",
      );
    };
    reserveSpace();
    const observer = new ResizeObserver(reserveSpace);
    if (bar.current) observer.observe(bar.current);
    window.addEventListener("resize", reserveSpace);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", reserveSpace);
      root.style.removeProperty("--ex-window-dock-height");
    };
  }, [windows.length]);

  if (windows.length === 0) return null;

  if (!desktop)
    return (
      <div
        ref={bar}
        role="region"
        aria-label={t("copy.443f816b565c")}
        className={className}
        data-window-dock-mobile
      >
        <details
          ref={selector}
          open={selectorOpen}
          onToggle={(event) => setSelectorOpen(event.currentTarget.open)}
          className="relative"
          onKeyDown={(event) => {
            if (event.key === "Escape" && selector.current?.open) {
              event.stopPropagation();
              selector.current.open = false;
              setSelectorOpen(false);
              selector.current.querySelector("summary")?.focus();
            }
          }}
        >
          <summary
            className={clsx(
              buttonClassName({ size: "sm", variant: "secondary" }),
              "h-10 max-w-full cursor-pointer list-none shadow-[var(--ex-shadow-overlay)] [&::-webkit-details-marker]:hidden",
            )}
          >
            <PanelsTopLeft className="size-4" aria-hidden="true" />
            {t("refinement.life.dock.windows", { count: windows.length })}
          </summary>
          <div
            hidden={!selectorOpen}
            style={{ display: selectorOpen ? undefined : "none" }}
            role="group"
            aria-label={t("copy.443f816b565c")}
            className="absolute bottom-[calc(100%+0.5rem)] left-0 grid max-h-[50dvh] w-72 max-w-[calc(100vw-1.5rem)] gap-1 overflow-y-auto rounded-xl border border-line bg-surface-raised p-1 shadow-[var(--ex-shadow-overlay)]"
          >
            {windows.map((entry) => (
              <div
                key={entry.id}
                className="flex min-w-0 items-center gap-1 rounded-lg hover:bg-surface-muted"
              >
                <button
                  type="button"
                  className="flex min-h-11 min-w-0 flex-1 flex-col px-2.5 py-2 text-left"
                  aria-label={t("copy.d07d06d4e2a6", { value1: entry.title })}
                  onClick={() => {
                    if (selector.current) selector.current.open = false;
                    setSelectorOpen(false);
                    entry.onRestore();
                  }}
                >
                  <span className="text-[11px] text-ink-faint">{entry.kind}</span>
                  <span className="w-full truncate text-sm text-ink">
                    {entry.title || t("copy.3ed7ccf473c8")}
                  </span>
                </button>
                {entry.onClose ? (
                  <IconButton
                    size="sm"
                    label={t("copy.72505bef1243", {
                      value1: entry.title || t("copy.38ed0d380a85"),
                    })}
                    onClick={entry.onClose}
                  >
                    <X className="size-3.5" />
                  </IconButton>
                ) : null}
              </div>
            ))}
          </div>
        </details>
      </div>
    );

  return (
    <div
      ref={bar}
      role="region"
      aria-label={t("copy.443f816b565c")}
      className={clsx(
        "pointer-events-auto flex max-w-full flex-wrap items-center gap-2",
        className,
      )}
    >
      <AnimatePresence initial={false}>
        {windows.map((entry) => (
          <motion.div
            key={entry.id}
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="flex min-w-0 max-w-[15rem] items-center gap-1 rounded-full border border-line bg-surface-raised py-1 pr-1 pl-2.5 shadow-[var(--ex-shadow-overlay)]"
          >
            <button
              type="button"
              onClick={entry.onRestore}
              className="cx-control flex min-w-0 items-center gap-2 rounded-full text-left"
              aria-label={t("copy.d07d06d4e2a6", { value1: entry.title })}
            >
              <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-accent" />
              <span className="min-w-0">
                <span className="block text-2xs font-medium tracking-[0.08em] text-ink-faint uppercase">
                  {entry.kind}
                </span>
                <span className="block truncate text-xs font-medium text-ink">
                  {entry.title || t("copy.3ed7ccf473c8")}
                </span>
              </span>
            </button>
            {entry.onClose ? (
              <IconButton
                size="sm"
                label={t("copy.72505bef1243", { value1: entry.title || t("copy.38ed0d380a85") })}
                onClick={entry.onClose}
              >
                <X className="size-3.5" aria-hidden="true" />
              </IconButton>
            ) : null}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
